import React from "react";
import { Oval } from "react-loader-spinner";
import moment from "moment";

import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { DatePicker } from "../../../../components";
import { getDailyStaxTransactions } from "../../reportsSlice";

import "./DailyStaxReportView.scss";

export const DailyStaxReportView: React.FC = () => {
  const dispatch = useAppDispatch();

  const getDailyStaxTransactionsAPI = useAppSelector(
    (state) => state.reports.getDailyStaxTransactionsAPI
  );
  const staxDailyTransactions = useAppSelector(
    (state) => state.reports.staxDailyTransactions
  );

  const [selectedDate, setSelectedDate] = React.useState<Date>(new Date());
  const [page, setPage] = React.useState<number>(1);

  React.useEffect(() => {
    const dateString = moment(selectedDate).format("YYYY-MM-DD");
    dispatch(
      getDailyStaxTransactions({
        page,
        date: dateString,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, page]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const renderTransactions = () => {
    if (!staxDailyTransactions) return null;
    return (
      <>
        <div className="transactions-list">
          <div className="transactions-header">
            <div className="header-item">Customer</div>
            <div className="header-item">Date</div>
            <div className="header-item">Card</div>
            <div className="header-item">Amount</div>
            <div className="header-item">Status</div>
            <div className="header-item">Type</div>
          </div>

          {staxDailyTransactions.transactions.map((transaction) => (
            <div className="transaction-row" key={transaction.id}>
              <div className="transaction-item customer-info">
                <div className="customer-name">{transaction.customerName}</div>
                <div className="customer-email">
                  {transaction.customerEmail}
                </div>
              </div>
              <div className="transaction-item date">
                {formatDate(transaction.createdAt)}
              </div>
              <div className="transaction-item card">
                **** {transaction.cardLastFour}
              </div>
              <div className="transaction-item amount">
                {formatCurrency(transaction.total)}
              </div>
              <div
                className={`transaction-item status ${transaction.status.toLowerCase()}`}
              >
                {transaction.status}
              </div>
              <div className="transaction-item type">{transaction.type}</div>
            </div>
          ))}
        </div>

        <div className="pagination">
          <button
            className="pagination-button"
            onClick={() => setPage((prev) => prev - 1)}
            disabled={!staxDailyTransactions.pagination.hasPrevPage}
          >
            Previous
          </button>

          <div className="pagination-info">
            Page {staxDailyTransactions.pagination.currentPage} of{" "}
            {staxDailyTransactions.pagination.lastPage}(
            {staxDailyTransactions.pagination.total} items)
          </div>

          <button
            className="pagination-button"
            onClick={() => setPage((prev) => prev + 1)}
            disabled={!staxDailyTransactions.pagination.hasNextPage}
          >
            Next
          </button>
        </div>
      </>
    );
  };

  return (
    <div className="daily-stax-report-view-component">
      <div className="title-container">
        <div className="title-row">
          <div className="title-text">Daily Stax Report</div>

          {getDailyStaxTransactionsAPI.loading && (
            <div style={{ marginLeft: 8 }}>
              <Oval
                color="#131313"
                height={20}
                width={20}
                secondaryColor="#0A60BB"
              />
            </div>
          )}
        </div>

        <DatePicker initialDate={selectedDate} onDateChange={setSelectedDate} />
      </div>

      {getDailyStaxTransactionsAPI.loading ? (
        <div className="loading">Loading transactions...</div>
      ) : staxDailyTransactions?.transactions.length === 0 ? (
        <div className="no-transactions">No transactions found</div>
      ) : (
        <div className="transactions-container">{renderTransactions()}</div>
      )}
    </div>
  );
};
